import Button from "@common/components/Button";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import MenuItem from "@common/components/MenuItem";
import { useErrorNotification } from "@common/components/Notification";
import RightDrawer from "@common/components/RightDrawer";
import Select from "@common/components/Select";
import Slider from "@common/components/Slider";
import { useSimpleReducer } from "@common/hooks";
import { Stack, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import { clearMessages, sendMessage } from "@services/meetings";
import {
  McuType,
  MeetingMessage,
  MeetingMessageColor,
  MeetingMessageFontSize,
  MeetingMessagePosition,
  MeetingMessageSpeed,
} from "@services/meetings/types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

type MessageAllProps = {
  open: boolean;
  onClose: Function;
};

const MessageAll = (props: MessageAllProps) => {
  const { open, onClose } = props;
  const context = React.useContext(RoomsRealtimeContext);
  const dispatchError = useErrorNotification();
  const { t } = useTranslation();
  const [openMore, setOpenMore] = useState<boolean>(false);
  const [message, dispatch] = useSimpleReducer<MeetingMessage>({
    message: "",
    color: MeetingMessageColor.white_font_on_red_background,
    fontSize: MeetingMessageFontSize.Small,
    repetitions: 3,
    speed: MeetingMessageSpeed.Slow,
    position: MeetingMessagePosition.Bottom,
    transparency: 50,
  });
  const maxLength = context.detail?.meeting?.mcuType === McuType.DMA ? 75 : 50;

  const confirmSendMessage = async () => {
    try {
      await sendMessage(context.detail?.meeting?.id, message);
      context.detail?.refetch();
      onClose(true);
    } catch (e) {
      dispatchError(e);
    }
  };

  const confirmClearMessage = async () => {
    try {
      await clearMessages(context.detail?.meeting?.id);
      context.detail?.refetch();
      dispatch({ message: "" });
      onClose();
    } catch (e) {
      dispatchError(e);
    }
  };

  return (
    <RightDrawer
      open={open}
      onClose={() => onClose(true)}
      title={t("Send Message to All")}
      closeButton={
        <Grid
          sx={(theme) => ({
            "&>.MuiButton-root": { marginRight: theme.spacing(1) },
          })}>
          <Button
            color="white"
            onClick={() => confirmSendMessage()}
            variant="contained"
          >
            Send
          </Button>
          <Button
            color="greyed"
            variant="outlined"
            onClick={() => onClose(false)}
          >
            Cancel
          </Button>
        </Grid>
      }
    >
      <Grid
        container
        direction="column"
        sx={{ width: '100%', paddingX: "1rem",
          gap: "1rem", }}
      >
        <FormControl>
          <TextField
            multiline
            rows={4}
            value={message.message}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              event.target.value.length <= maxLength
                ? dispatch({ message: event.target.value })
                : ""
            }
          />
          <FormHelperText>
            {message.message.length}/{maxLength}
          </FormHelperText>
        </FormControl>
        {context.detail?.meeting?.mcuType !== McuType.DMA && (
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="text"
              color="primary"
              onClick={() => setOpenMore(!openMore)}
              sx={{ width: "fit-content" }}
            >
              {openMore ? "Less" : "More"} options
            </Button>
            <Button
              variant="text"
              color="error"
              onClick={() => confirmClearMessage()}
              sx={{
                width: "fit-content",
                color: ({ palette }) => palette.common.grey.g828282,
              }}
            >
              Clear Messages
            </Button>
          </Stack>
        )}
        {openMore && (
          <>
            <Select
              label={t("Font Size")}
              value={message.fontSize}
              onChange={(e) =>
                dispatch({ fontSize: e.target.value as MeetingMessageFontSize })
              }
            >
              {Object.keys(MeetingMessageFontSize).map((size, i) => (
                <MenuItem key={i} value={size}>
                  {t(size)}
                </MenuItem>
              ))}
            </Select>
            <Select
              label={t("Repeat")}
              value={message.repetitions}
              onChange={(e) =>
                dispatch({ repetitions: e.target.value as number })
              }
            >
              {Array.from(Array(20).keys()).map((i) => (
                <MenuItem key={i} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
            <Select
              label={t("Speed")}
              value={message.speed}
              onChange={(e) =>
                dispatch({ speed: e.target.value as MeetingMessageSpeed })
              }
            >
              {Object.keys(MeetingMessageSpeed).map((speed, i) => (
                <MenuItem key={i} value={speed}>
                  {t(speed)}
                </MenuItem>
              ))}
            </Select>
            <Select
              label={t("Position")}
              value={message.position}
              onChange={(e) =>
                dispatch({ position: e.target.value as MeetingMessagePosition })
              }
            >
              {Object.keys(MeetingMessagePosition).map((position, i) => (
                <MenuItem key={i} value={position}>
                  {t(position)}
                </MenuItem>
              ))}
            </Select>
            <Slider
              title={t("Transparency")}
              labelstart={"0"}
              labelend={"100"}
              min={0}
              max={100}
              value={message.transparency}
              valueLabelDisplay="auto"
              onChange={(_, value) =>
                dispatch({ transparency: value as number })
              }
            />
          </>
        )}
      </Grid>
    </RightDrawer>
  );
};

export default MessageAll;
